import { Injectable, computed, inject, signal } from '@angular/core';

import { TranslateService } from '@ngx-translate/core';

import { NoteConcept, Article } from '@shared-models';

import { NoteArticleType } from '../enums/note-article-type.enum';

@Injectable()
export class NoteFormStateService {
  private readonly translate = inject(TranslateService);
  // State Signals
  readonly formArrayVersion = signal(0);
  readonly concepts = signal<NoteConcept[]>([]);
  readonly selectedConcept = signal<string>(
    this.translate.instant('global.notes.select-concept'),
  );
  readonly selectedArticleType = signal<number>(NoteArticleType.BY_ARTICLE);
  readonly currentPage = signal<number>(0);
  readonly itemsPerPage = signal<number>(1);
  readonly selectedSaleIndex = signal<number>(0);
  readonly isLoadingConcepts = signal<boolean>(false);
  readonly searchedArticles = signal<Article[]>([]);
  readonly selectedArticle = signal<Article | null>(null);
  readonly searchValue = signal<string>('');
  readonly priceLockedByQuantity = signal<Set<number>>(new Set());
  readonly quantityLockedByPrice = signal<Set<number>>(new Set());
  readonly priceMode = signal<Map<number, 'absolute' | 'percentage'>>(
    new Map(),
  );

  // Computed Options
  readonly conceptOptions = computed(() =>
    this.concepts().map((concept) => ({
      value: concept.code,
      label: concept.description,
    })),
  );

  getPriceMode(index: number): 'absolute' | 'percentage' {
    return this.priceMode().get(index) || 'absolute';
  }

  togglePriceMode(index: number): void {
    const currentMode = this.priceMode().get(index) || 'absolute';
    const newMode = currentMode === 'absolute' ? 'percentage' : 'absolute';
    this.priceMode.update((map) => new Map(map).set(index, newMode));
  }

  incrementFormArrayVersion(): void {
    this.formArrayVersion.update((v) => v + 1);
  }

  resetArticleSearch(): void {
    this.selectedArticle.set(null);
    this.searchedArticles.set([]);
    this.currentPage.set(0);
  }

  resetState(defaultConceptPlaceholder: string): void {
    this.searchValue.set('');
    this.searchedArticles.set([]);
    this.selectedArticle.set(null);
    this.selectedConcept.set(defaultConceptPlaceholder);
    this.selectedArticleType.set(NoteArticleType.BY_ARTICLE);
    this.currentPage.set(0);
  }
}
